import {error} from "@angular/compiler-cli/src/transformers/util";
import { Injectable, InjectionToken } from "@angular/core";
import { BehaviorSubject, finalize, Observable, take, tap } from "rxjs"
import {LEGAL_HUB_URL} from "../../legal-hub-consts";
import { PlaybooksListeningFacadeService } from "../../store/playbooks-listening.facade.service";
import { IPlaybooksItemDTO, IUpdateGuidelineDTO, IUpdatePlaybookGuidelineDTO, PlaybookStatusEnum } from "../../dto/playbooks-listening.dto";
import { PlaybookListeningApiService } from "../../playbook-listening.api.service";
import { FormArray, FormGroup, UntypedFormBuilder, Validators } from "@angular/forms";
import {Actions, ofType} from "@ngrx/effects";
import {TranslocoService} from '@jsverse/transloco';

import * as PlaybooksListingActions from "./../../store/playbooks.actions";
import { Router } from "@angular/router";
import { GlobalService } from "app/global/global.service";
import { Store } from "@ngrx/store";



export interface IAddPlaybookFacade extends IAddEditPlaybookFacade {}

export interface IEditPlaybookFacade extends IAddEditPlaybookFacade {
    onPlaybookLoad<T>(form:FormGroup, playbookId?:number):void
}


export interface IAddEditPlaybookFacade {
    loading$:Observable<boolean>
    submitting$:Observable<boolean>
    onSubmit<T>(form:T, playbookId:number),
    updateGuidelineForm(playbookForm, guidelines):void
}

export interface IAddEditPlaybookForm {
    name: string,
    category: number,
    purpose: string,
    usage: string,
    status: boolean,
    color:string,
    guidelines:[]
}

 
export const IAddEditPlaybookFacadeToken = new InjectionToken<IAddEditPlaybookFacade>('IAddEditPlaybookFacadeToken');

@Injectable({
    providedIn: 'root' 
})
export class EditPlaybookFacadeCommon{

    constructor(protected _formBuilder: UntypedFormBuilder) {
    }

    updateGuidelineForm(playbookForm, guidelines):void{
       const sortedGuidelines = [...guidelines ?? []].sort((a, b) => a.order - b.order);  
        (<FormArray>playbookForm?.get('guidelines')). clear();  
         sortedGuidelines.forEach((x, i) => {
             this.addNewGuideline(x.id, x.playbook_id, x.content, i, playbookForm);
         });
    }

    
    protected addNewGuideline(id:number, playbook_id:number, content:string, index:number, form?: FormGroup) {
        (<FormArray>form?.get('guidelines'))
        .push(this._formBuilder.group({
            id:[id ],
            playbook_id: [playbook_id],
            content: [content, [Validators.required, Validators.minLength(1)]],
            order: [index, Validators.required]
        }))
    }
}


@Injectable({
    providedIn: 'root' 
})
export class EditPlaybookFacade extends EditPlaybookFacadeCommon implements IEditPlaybookFacade {
    loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    submitting$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);


    constructor(private _playbooksListeningFacadeService: PlaybooksListeningFacadeService,
                private playbookListeningApiService: PlaybookListeningApiService,
                private _actions$: Actions,
                private _globalService:GlobalService,
                private _translocoService:TranslocoService,
                private _router:Router,
                private _store:Store,
                _formBuilder: UntypedFormBuilder,
    ) {
        super(_formBuilder);
    }

    onPlaybookLoad(form, playbookId): void {
        this.initializePlaybook(playbookId, form);
    }

    onSubmit<T extends Partial<IAddEditPlaybookForm>>(form: T, playbookId:number): void {

        this.submitting$.next(true);

        this._actions$.
        pipe(
            ofType(PlaybooksListingActions.updatePlaybookSucceeded ,PlaybooksListingActions.updatePlaybookFailed ),
            take(1)
        ).subscribe((action) => {
            if(PlaybooksListingActions.updatePlaybookSucceeded.type === action.type){
                const playbookId = (<any>action).playbooksItem.id;
                  if(form.guidelines?.length > 0){
                    this._playbooksListeningFacadeService.updateGuideline(playbookId, form.guidelines as unknown as IUpdateGuidelineDTO);
                }
                this._globalService.toastrSuccess(this._translocoService.translate('Playbook edited toast message (legal hub playbooks listening) - Playbook edited'));
                this.submitting$.next(false);
            }
        })
     
     
     
       this._playbooksListeningFacadeService.updatePlaybook(
            playbookId,
            {
               name: form.name,
               playbook_category_id: form.category,
               purpose: form.purpose,
               usage: form?.usage,
               status: form.status === false ? PlaybookStatusEnum.draft : PlaybookStatusEnum.published,
               color:form.color,
           } as IUpdatePlaybookGuidelineDTO );
 
      }


     private initializePlaybook(playbookId: number, playbookForm:FormGroup){
        this.loading$.next(true);
        this.playbookListeningApiService.getPlaybookById(playbookId)
        .pipe(
            finalize(()=> this.loading$.next(false)),
            take(1)
        )
        .subscribe(
         {
             next: (playbook)=>
                 {
                     const playbookFormData = {
                        name: playbook?.name,
                        category: playbook?.playbook_category_id,
                        purpose: playbook?.purpose,
                        usage: playbook?.usage,
                        status: playbook?.status === PlaybookStatusEnum.draft ? 0 : 1,
                        color: playbook?.color
                     }
        
                     const guidelines = playbook?.playbook_guidelines?.sort((a, b) => a.order - b.order);
        
                     this._store.dispatch(PlaybooksListingActions.addNewCategorySucceeded(playbook?.playbook_category));
        
                     playbookForm.patchValue(playbookFormData);
                     guidelines.forEach((x, i) => {
                         this.addNewGuideline(x.id, x.playbook_id, x.content, i, playbookForm);
                     });
                 },
             error: (err)=>
                 {
                     if(err?.status === 404){
                         this._globalService.toastrError(this._translocoService.translate("Playbook not found toast message (edit playbook) - Playbook not found"));
                     }else{
                         this._globalService.toastrError(err);
                     }
                 }
        })
    }

}

@Injectable({
    providedIn: 'root' 
})
export class AddPlaybookFacade extends EditPlaybookFacadeCommon  implements IAddPlaybookFacade {
    loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    submitting$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(private _playbooksListeningFacadeService: PlaybooksListeningFacadeService,
               private _actions$: Actions,
               private _globalService:GlobalService,
               private _translocoService:TranslocoService,
               private _router:Router,
              _formBuilder: UntypedFormBuilder) {
                super(_formBuilder);
               }

    onSubmit<T extends Partial<IAddEditPlaybookForm>>(form: T): void {

        this._actions$.
        pipe(
            ofType(PlaybooksListingActions.addNewPlaybookSucceeded ,PlaybooksListingActions.addNewPlaybookFailed ),
            take(1)
        ).subscribe((action) => {
          if(PlaybooksListingActions.addNewPlaybookSucceeded.type === action.type){
            const playbookId = (<any>action).playbooksItem.id;

            if(form.guidelines?.length > 0){

                const guidelines = (form.guidelines as unknown as IUpdateGuidelineDTO[]
                    ).map((x, i) => ({ 
                    ...x,
                    playbook_id: playbookId,
                     }));


                this._playbooksListeningFacadeService.updateGuideline(playbookId, guidelines as unknown as IUpdateGuidelineDTO);
            }

            this._globalService.toastrSuccess(this._translocoService.translate('Playbook added toast message (legal hub playbooks listening) - Playbook added'));
            this._router.navigate([LEGAL_HUB_URL])
          }
        })


             this._playbooksListeningFacadeService.addPlaybook(
            {
               name: form.name,
               playbook_category_id: form.category,
               purpose: form.purpose,
               usage: form?.usage,
               status: form.status === false ? PlaybookStatusEnum.draft : PlaybookStatusEnum.published,
               color:form.color,
           } as IUpdatePlaybookGuidelineDTO );

           
    }
}